import { InfiniteData, useInfiniteQuery, UseInfiniteQueryOptions } from '@tanstack/react-query';
import { AxiosRequestConfig } from 'axios';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import { useEffect, useMemo, useState } from 'react';

import { isBuildOnHold, PublicApiBuild } from '@/models/Build';
import { PublicApiPagedResponse } from '@/models/network';
import BuildListFilters from '@/pages/App/AppDetails/types/BuildListFilters';
import { publicApi } from '@/utils/api';
import { snakeCaseKeys } from '@/utils/changeCase';

const buildKeys = {
  lists: () => ['builds', 'list'],
  list: ({ appSlug, filters, isOnHold, limit }: UseBuildsParams) => [
    ...buildKeys.lists(),
    { appSlug, ...filters, isOnHold, limit },
  ],
};

type UseBuildsParams = {
  appSlug: string;
  filters: BuildListFilters;
  isOnHold?: boolean;
  limit?: number;
};

type BuildsResponse = PublicApiPagedResponse<PublicApiBuild>;

const getBuilds = (data?: InfiniteData<BuildsResponse>): PublicApiBuild[] | undefined =>
  data?.pages.map((p) => p.data).reduce((acc, curr) => [...acc, ...curr], []);

const useBuilds = (
  { appSlug, filters, isOnHold, limit }: UseBuildsParams,
  options?: Omit<
    UseInfiniteQueryOptions<BuildsResponse>,
    'queryFn' | 'queryKey' | 'getNextPageParam' | 'initialPageParam' | 'select'
  > & {
    requestConfig?: Omit<AxiosRequestConfig, 'url'>;
  },
) => {
  const [isFetchingNextPageTracking, setIsFetchingNextPageTracking] = useState(false);
  const [isFetchingNextPageError, setIsFetchingNextPageError] = useState(false);

  useEffect(() => {
    setIsFetchingNextPageTracking(false);
    setIsFetchingNextPageError(false);
  }, [appSlug, filters, isOnHold, limit]);

  const infiniteQueryResult = useInfiniteQuery<BuildsResponse>({
    queryKey: buildKeys.list({ appSlug, filters, isOnHold, limit }),
    queryFn: async ({ pageParam }) => {
      const buildNumberRegexp = /^[0-9]+$/;
      const formattedFilters: any = {
        branch: filters.branch,
        buildNumber: buildNumberRegexp.test(filters.term) ? filters.term : undefined,
        commitMessage: buildNumberRegexp.test(filters.term) || !filters.term ? undefined : filters.term,
        status: filters.status !== null ? filters.status : undefined,
        before: filters.beforeDate ? filters.beforeDate : undefined,
        after: filters.afterDate ? filters.afterDate : undefined,
        workflow: undefined,
        pipeline: undefined,
        artifactType: filters.artifactType,
      };

      switch (filters.triggerType) {
        case 'push':
          formattedFilters.trigger_event_type = 'push';
          break;
        case 'pull-request':
          formattedFilters.trigger_event_type = 'pull-request';
          break;
        case 'tag':
          formattedFilters.trigger_event_type = 'tag';
          break;
      }

      if (filters.pipelineable) {
        if (filters.pipelineable.startsWith('workflow-')) {
          formattedFilters.workflow = filters.pipelineable.replace(/^(workflow-)/, '');
        } else if (filters.pipelineable.startsWith('pipeline-')) {
          formattedFilters.pipeline = filters.pipelineable.replace(/^(pipeline-)/, '');
        }
      }

      const queryParams = new URLSearchParams(
        snakeCaseKeys(
          omitBy(
            {
              ...formattedFilters,
              isOnHold,
              limit,
              before: pageParam || formattedFilters.before,
            },
            isNil,
          ),
        ) as any,
      ).toString();

      const basePath = `/v0.1/apps/${appSlug}/pipelines`;
      const { data } = await publicApi.get<BuildsResponse>(`${basePath}?${queryParams}`, options?.requestConfig);
      return data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage?.paging?.next,
    ...options,
  });

  useEffect(() => {
    if (infiniteQueryResult.error) {
      if (isFetchingNextPageTracking) {
        setIsFetchingNextPageError(!!infiniteQueryResult.error);
        setIsFetchingNextPageTracking(false);
      }
    }
  }, [infiniteQueryResult.error, isFetchingNextPageTracking]);

  const { data, isFetchingNextPage } = infiniteQueryResult;

  useEffect(() => {
    if (isFetchingNextPage) {
      setIsFetchingNextPageError(false);
      setIsFetchingNextPageTracking(true);
    }
  }, [isFetchingNextPage, isFetchingNextPageTracking]);

  const builds = useMemo(() => getBuilds(data), [data]);
  const count = builds?.length || 0;
  const queuedCount = builds?.filter(isBuildOnHold).length || 0;

  return {
    ...infiniteQueryResult,
    isFetchingNextPageError,
    builds,
    count,
    queuedCount,
  };
};

export default useBuilds;
